import { Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import type { EnhancedSelectOption } from "@/types/ui/component-props";

/**
 * Props for encounter type section.
 * @agentic connections
 */
export type EncounterTypeSectionProps = {
  duplicateIntakeEncounterId?: string;
  value: string;
  hasLegacyEncounterType: boolean;
  allEncounterTypeOptions: EnhancedSelectOption[];
  currentEncounterTypeOptions: EnhancedSelectOption[];
  /** When set, only these encounter type codes are shown (activity-scoped restriction). */
  allowedTypes?: string[];
  error?: string;
  readOnly?: boolean;
  onChange: (value: string) => void;
};

/**
 * Encounter type selection section with optional intake duplication warning.
 * @agentic connections
 */
export const EncounterTypeSection = ({
  duplicateIntakeEncounterId,
  value,
  hasLegacyEncounterType,
  allEncounterTypeOptions,
  currentEncounterTypeOptions,
  allowedTypes,
  error,
  readOnly = false,
  onChange,
}: EncounterTypeSectionProps) => {
  const baseOptions = hasLegacyEncounterType
    ? allEncounterTypeOptions
    : currentEncounterTypeOptions;

  const displayOptions = allowedTypes
    ? baseOptions.filter((opt) => allowedTypes.includes(opt.value))
    : baseOptions;

  return (
    <FormField
      label="Interaction Type"
      required
      variant="section"
      showDivider={false}
      readOnly={readOnly}
      error={error}
    >
      {duplicateIntakeEncounterId && (
        <Alert
          color="red"
          radius="md"
          mb="sm"
          icon={<IconInfoCircle size={18} />}
        >
          A recent intake interaction already exists for this patient. Selecting
          another intake may duplicate documentation.
        </Alert>
      )}
      <CardSelect
        placeholder="Select interaction type"
        value={value || ""}
        onChange={(nextValue) => {
          const singleValue = Array.isArray(nextValue)
            ? nextValue[0]
            : nextValue;
          onChange(singleValue || "");
        }}
        data={displayOptions}
        columns={2}
        error={!!error}
      />
    </FormField>
  );
};
